import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { avanzoApi, FEEDBACK_TITLES, REASON_COPY } from "@/lib/avanzoApi";
import BuddyReflectionCard from "./BuddyReflectionCard";
import BuddyTutorCard from "./BuddyTutorCard";

/**
 * /avanzo/entrenar — TRAINING loop con gobernanza de tiempo (Fase 5A.1).
 *
 * State machine:
 *   loading → answering → submitting → feedback → (continue|show_warning) → …
 *   … → complete (resumen de sesión)
 *   … → cooldown (bloqueado 30 min tras max_active_time)
 *   … → ended (cierre voluntario)
 *
 * Reglas UX:
 *   - Sin cronómetro grande. Solo un chip discreto "Sesión en curso".
 *   - Avisos 30 y 40 min NO interrumpen un reactivo en curso: se
 *     mantienen pending hasta después de confirmar la respuesta actual.
 *   - Cierre a 45 min: pantalla amable con resumen (nada de "tiempo
 *     agotado" / "bloqueado").
 *   - Backend permanece autoridad del active_training_seconds.
 */
const HEARTBEAT_INTERVAL_MS = 20_000;    // ~20s con visibilidad + input
const INPUT_RECENCY_WINDOW_MS = 25_000;  // señal de actividad reciente

export default function AvanzoTrain() {
  const [sessionId, setSessionId] = useState(null);
  const [item, setItem] = useState(null);
  const [target, setTarget] = useState(null);
  const [transferCtx, setTransferCtx] = useState(false);
  const [choice, setChoice] = useState(null);
  const [phase, setPhase] = useState("loading");
  // loading | answering | submitting | feedback |
  // complete | cooldown | ended | error
  const [feedback, setFeedback] = useState(null);
  const [sessionLog, setSessionLog] = useState([]);
  const [error, setError] = useState(null);
  const [pendingWarning, setPendingWarning] = useState(null); // "warn_30"|"warn_40"|null
  const [activeWarning, setActiveWarning] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [completionSummary, setCompletionSummary] = useState(null);
  const [dayMode, setDayMode] = useState(null); // Fase 8
  // Fase 8.1 — Buddy Reflection Layer (determinístico, 0 LLM)
  const [buddyReflection, setBuddyReflection] = useState(null);
  const [buddyShown, setBuddyShown] = useState(0);
  // Fase 8.2 — Buddy Tutor Mode
  const [tutorOffer, setTutorOffer] = useState(null);   // {itemId, subject}
  const [tutorActive, setTutorActive] = useState(false);

  const startTsRef       = useRef(null);
  const submittingRef    = useRef(false);
  const lastInputTsRef   = useRef(Date.now());
  const sessionIdRef     = useRef(null);
  const currentPhaseRef  = useRef("loading");

  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { currentPhaseRef.current = phase; }, [phase]);

  // Fase 8 — cargar modo del día en montaje
  useEffect(() => {
    let cancelled = false;
    avanzoApi.trainingMode()
      .then((r) => { if (!cancelled) setDayMode(r.data); })
      .catch(() => { if (!cancelled) setDayMode(null); });
    return () => { cancelled = true; };
  }, []);

  // Registrar input reciente para heartbeat honesto
  useEffect(() => {
    const bump = () => { lastInputTsRef.current = Date.now(); };
    const events = ["click", "keydown", "pointerdown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, bump));
  }, []);

  // Heartbeat cada ~20s solo con visibilidad + input reciente
  useEffect(() => {
    const id = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastInputTsRef.current > INPUT_RECENCY_WINDOW_MS) return;
      const sid = sessionIdRef.current;
      if (!sid) return;
      // No enviar heartbeat cuando ya cerramos o en cooldown
      const ph = currentPhaseRef.current;
      if (["complete", "cooldown", "ended", "error"].includes(ph)) return;
      try {
        const r = await avanzoApi.trainingSessionHeartbeat({ session_id: sid });
        const d = r.data || {};
        if (d.training_session_complete) {
          setCompletionSummary({
            session_id: sid,
            active_training_seconds: d.active_training_seconds,
            items_completed: (completionSummary?.items_completed) ?? 0,
          });
          setPhase("complete");
          return;
        }
        if (d.warning_pending && !activeWarning) {
          setPendingWarning(d.warning_pending);
        }
      } catch (_) { /* silencioso, backend autoritativo */ }
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [activeWarning, completionSummary]);

  const applySessionMeta = useCallback((meta) => {
    if (!meta) return;
    if (meta.session_id) setSessionId(meta.session_id);
    if (meta.warning_pending && !activeWarning) {
      setPendingWarning(meta.warning_pending);
    }
  }, [activeWarning]);

  const loadNext = useCallback(async () => {
    setPhase("loading");
    setChoice(null);
    setFeedback(null);
    setTutorOffer(null);
    setTutorActive(false);
    try {
      const r = await avanzoApi.trainingNext(
        sessionIdRef.current ? { session_id: sessionIdRef.current } : {}
      );
      const d = r.data || {};
      // Cierre por límite / cooldown / quota diaria o semanal
      if (d.training_session_complete) {
        if (d.completion_reason === "cooldown_active") {
          setCooldownRemaining(Number(d.cooldown_remaining_s) || 0);
          setPhase("cooldown");
          return;
        }
        if (d.completion_reason === "daily_quota_completed") {
          setCompletionSummary({ message: d.message, kind: "daily" });
          setPhase("daily_complete");
          return;
        }
        if (d.completion_reason === "weekly_quota_completed") {
          setCompletionSummary({ message: d.message, kind: "weekly" });
          setPhase("weekly_complete");
          return;
        }
        setCompletionSummary(d.summary || null);
        setPhase("complete");
        return;
      }
      applySessionMeta(d.session_meta);
      setItem(d.item);
      setTarget(d.target);
      setTransferCtx(!!d.transfer_context);
      startTsRef.current = Date.now();
      lastInputTsRef.current = Date.now();
      setPhase("answering");
    } catch (e) {
      setError(e);
      setPhase("error");
    }
  }, [applySessionMeta]);

  useEffect(() => { loadNext(); }, [loadNext]);

  const submit = async () => {
    if (!choice || submittingRef.current) return;
    submittingRef.current = true;
    setPhase("submitting");
    const rt = startTsRef.current ? (Date.now() - startTsRef.current) / 1000 : null;
    try {
      const r = await avanzoApi.trainingAnswer({
        session_id: sessionIdRef.current,
        item_id: item.id,
        student_answer: choice,
        response_time_seconds: rt,
        transfer_context: transferCtx,
      });
      setFeedback(r.data);
      // Fase 8.2 — Tras respuesta incorrecta, evaluar si ofrecer Tutor
      if (r.data && !r.data.correct && item?.id) {
        try {
          const st = await avanzoApi.tutorStatus({
            item_id: item.id,
            session_id: sessionIdRef.current,
            last_answer_incorrect: "true",
          });
          if (st.data?.offer === true) {
            setTutorOffer({ itemId: item.id, subject: item.subject });
            setTutorActive(true);
          }
        } catch (_) { /* silencioso */ }
      }
      setSessionLog((log) => [
        ...log,
        {
          skill: item.skill,
          subjectLabel: subjectLabel(item.subject),
          correct: r.data.correct,
        },
      ]);
      // Si el backend cerró la sesión (por respuesta que cruzó el 45 min)
      if (r.data.training_session_closed_after_answer) {
        setCompletionSummary({
          items_completed: (completionSummary?.items_completed || 0) + 1,
        });
        setPhase("complete");
        return;
      }
      setPhase("feedback");
    } catch (e) {
      setError(e);
      setPhase("error");
    } finally {
      submittingRef.current = false;
    }
  };

  const continueAfterFeedback = async () => {
    // Aviso pendiente: mostrar ANTES del siguiente reactivo
    if (pendingWarning) {
      setActiveWarning(pendingWarning);
      setPendingWarning(null);
      return;
    }
    // Fase 8.1 — Poll Buddy Reflection status entre reactivos (nunca durante)
    // Cap 2/sesión aplicado en backend. Sólo intentamos si aún no llegamos a 2.
    // Fase 8.2 — Si Tutor está activo, suprimir Reflection en ese momento.
    if (!tutorActive && buddyShown < 2 && sessionIdRef.current) {
      try {
        const r = await avanzoApi.buddyStatus(sessionIdRef.current);
        const d = r.data || {};
        if (d.offer === true && d.type) {
          try {
            const bs = await avanzoApi.buddyStart({
              session_id: sessionIdRef.current,
              source: "training",
              reflection_type: d.type,
              trigger_reason: d.reason,
            });
            if (bs?.data?.reflection_id) {
              setBuddyReflection(bs.data);
              setBuddyShown((n) => n + 1);
              return; // No cargar siguiente reactivo mientras se muestra buddy
            }
          } catch (_) { /* silencioso, no bloquear entrenamiento */ }
        }
      } catch (_) { /* silencioso */ }
    }
    await loadNext();
  };

  const closeBuddy = async () => {
    setBuddyReflection(null);
    await loadNext();
  };

  const acknowledgeWarning = async (kind, mode) => {
    // mode: "continue" | "finish"
    try {
      if (sessionIdRef.current) {
        await avanzoApi.trainingSessionWarnAck({
          session_id: sessionIdRef.current, kind,
        });
      }
    } catch (_) { /* silencioso */ }
    setActiveWarning(null);
    if (mode === "finish") {
      await finishSessionVoluntary("recommended_finish");
    } else {
      await loadNext();
    }
  };

  const finishSessionVoluntary = async (reason = "student_finished") => {
    try {
      const r = await avanzoApi.trainingSessionFinish({
        session_id: sessionIdRef.current, reason,
      });
      setCompletionSummary({
        session_id: r.data.session_id || sessionIdRef.current,
        active_training_seconds: r.data.active_training_seconds,
        items_completed: r.data.items_completed,
        subjects_practiced: r.data.subjects_practiced,
      });
    } catch (_) { /* backend puede haber cerrado ya */ }
    setPhase("ended");
  };

  // ── Render ────────────────────────────────────────────────────
  if (phase === "loading") {
    return (
      <div className="avz-empty" data-testid="avz-train-loading">
        Cargando siguiente actividad…
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div className="avz-card" data-testid="avz-train-error">
        <h2 className="avz-h2">No pudimos cargar esta actividad.</h2>
        <p className="avz-mute">Puedes intentarlo nuevamente.</p>
        <button
          className="avz-btn avz-btn-primary avz-mt-4"
          onClick={loadNext}
          data-testid="avz-retry"
        >
          Reintentar
        </button>
      </div>
    );
  }
  if (phase === "cooldown") {
    return (
      <CooldownCard remainingSeconds={cooldownRemaining} />
    );
  }
  if (phase === "daily_complete") {
    return (
      <QuotaCompleteCard kind="daily"
                          message={completionSummary?.message} />
    );
  }
  if (phase === "weekly_complete") {
    return (
      <QuotaCompleteCard kind="weekly"
                          message={completionSummary?.message} />
    );
  }
  if (phase === "complete") {
    return (
      <SessionCompleteCard log={sessionLog} summary={completionSummary} />
    );
  }
  if (phase === "ended") {
    return (
      <SessionCompleteCard log={sessionLog} summary={completionSummary}
                            voluntary />
    );
  }

  // Fase 8.1 — Buddy Reflection entre reactivos (nunca durante reactivo)
  if (buddyReflection) {
    return (
      <div data-testid="avz-train-buddy-view">
        {dayMode && dayMode.mode !== "NORMAL" && (
          <TrainModeBanner mode={dayMode} />
        )}
        <div className="avz-row-between avz-mt-4" style={{ marginBottom: 10 }}>
          <span className="avz-mute">Un momento breve</span>
          <span className="avz-chip" data-testid="avz-session-chip">
            Sesión en curso
          </span>
        </div>
        <BuddyReflectionCard
          reflection={buddyReflection}
          onClose={closeBuddy}
          variant="training"
        />
      </div>
    );
  }

  return (
    <div data-testid="avz-train">
      {dayMode && dayMode.mode !== "NORMAL" && (
        <TrainModeBanner mode={dayMode} />
      )}
      {/* Aviso modal cuando corresponda (nunca durante reactivo) */}
      {activeWarning === "warn_30" && (
        <WarningModal
          testid="avz-warn-30"
          title="¡Buen trabajo!"
          body={"Ya completaste una buena sesión de práctica. Puedes terminar por hoy o continuar un poco más."}
          onContinue={() => acknowledgeWarning("warn_30", "continue")}
          onFinish={() => acknowledgeWarning("warn_30", "finish")}
        />
      )}
      {activeWarning === "warn_40" && (
        <WarningModal
          testid="avz-warn-40"
          title="Estás por completar tu sesión de hoy"
          body={"Terminaremos la práctica al llegar a 45 minutos para mantener sesiones de estudio enfocadas."}
          onContinue={() => acknowledgeWarning("warn_40", "continue")}
          onFinish={() => acknowledgeWarning("warn_40", "finish")}
          continueLabel="Continuar"
        />
      )}

      {/* Meta */}
      <div className="avz-row-between avz-mt-4" style={{ marginBottom: 10 }}>
        <span
          className="avz-mute"
          data-testid="avz-train-subject"
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          <span
            className="avz-subject-dot"
            aria-hidden="true"
            style={{ background: `var(--avz-subject-${item.subject})` }}
          />
          {subjectLabel(item.subject)}
        </span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span className="avz-chip" data-testid="avz-session-chip">
            Sesión en curso
          </span>
          {target?.reason_selected === "spaced_revisit" && (
            <span className="avz-chip" data-testid="avz-revisit-chip">
              {REASON_COPY.spaced_revisit}
            </span>
          )}
        </div>
      </div>

      <section className="avz-card">
        {item.context && (
          <p className="avz-lead" data-testid="avz-train-context">
            {item.context}
          </p>
        )}
        <h2 className="avz-h2 avz-mt-4" data-testid="avz-train-question">
          {item.question}
        </h2>

        <div className="avz-stack avz-mt-4" role="radiogroup"
             aria-label="Opciones de respuesta">
          {["A", "B", "C", "D"].map((letter) => (
            <button
              key={letter}
              type="button"
              className="avz-option"
              aria-pressed={choice === letter}
              disabled={phase !== "answering"}
              onClick={() => setChoice(letter)}
              data-testid={`avz-option-${letter}`}
            >
              <span className="avz-option-letter">{letter}</span>
              <span>{item.options[letter]}</span>
            </button>
          ))}
        </div>

        {phase === "feedback" && feedback && (
          <FeedbackBox
            feedback={feedback}
            onContinue={continueAfterFeedback}
            onStop={() => finishSessionVoluntary("student_finished")}
          />
        )}

        {/* Fase 8.2 — Buddy Tutor: oferta o sesión activa tras respuesta
            incorrecta. NUNCA aparece durante un reactivo en curso. */}
        {phase === "feedback" && feedback && !feedback.correct
          && (tutorOffer || tutorActive) && (
          <BuddyTutorCard
            key={tutorOffer?.itemId || item?.id}
            itemId={item?.id}
            trainingSessionId={sessionIdRef.current}
            subject={item?.subject}
            initialTrigger="post_incorrect"
            onFinished={() => {
              setTutorOffer(null);
              setTutorActive(false);
            }}
          />
        )}

        {phase !== "feedback" && (
          <div className="avz-row-between avz-mt-8">
            <button
              className="avz-btn avz-btn-ghost"
              onClick={() => finishSessionVoluntary("student_finished")}
              data-testid="avz-stop"
            >
              Detenerme aquí
            </button>
            <button
              className="avz-btn avz-btn-primary"
              onClick={submit}
              disabled={!choice || phase === "submitting"}
              data-testid="avz-submit"
            >
              {phase === "submitting" ? "Enviando…" : "Enviar respuesta"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function FeedbackBox({ feedback, onContinue, onStop }) {
  const level = feedback.feedback_level
    || (feedback.correct ? "acknowledgement" : "hint");
  const title = FEEDBACK_TITLES[level]
    || (feedback.correct ? "Correcto" : "Todavía no");
  return (
    <div
      className="avz-feedback avz-mt-4"
      data-tone={level}
      data-testid="avz-feedback"
      role="status"
      aria-live="polite"
    >
      {title && (
        <div className="avz-fb-title" data-testid="avz-fb-title">{title}</div>
      )}
      <p data-testid="avz-fb-text">{feedback.feedback}</p>
      {level === "explicit_teaching" && (
        <p className="avz-mute avz-mt-4" data-testid="avz-fb-transfer-note">
          Más adelante volveremos a practicar esta habilidad en otro contexto.
        </p>
      )}
      <div className="avz-row-between avz-mt-4">
        <button
          className="avz-btn avz-btn-ghost"
          onClick={onStop}
          data-testid="avz-fb-stop"
        >
          Detenerme aquí
        </button>
        <button
          className="avz-btn avz-btn-primary"
          onClick={onContinue}
          data-testid="avz-fb-continue"
        >
          {feedback.correct ? "Continuar" : "Siguiente actividad"}
        </button>
      </div>
    </div>
  );
}

function WarningModal({ testid, title, body, onContinue, onFinish,
                        continueLabel = "Continuar practicando" }) {
  return (
    <div
      className="avz-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testid}-title`}
      data-testid={testid}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 60, padding: 16,
      }}
    >
      <div className="avz-card" style={{ maxWidth: 420, width: "100%" }}>
        <h2 id={`${testid}-title`} className="avz-h2"
            data-testid={`${testid}-title`}>
          {title}
        </h2>
        <p className="avz-lead avz-mt-4" data-testid={`${testid}-body`}>
          {body}
        </p>
        <div className="avz-row-between avz-mt-8">
          <button
            className="avz-btn avz-btn-ghost"
            onClick={onFinish}
            data-testid={`${testid}-finish`}
          >
            Finalizar mi sesión
          </button>
          <button
            className="avz-btn avz-btn-primary"
            onClick={onContinue}
            data-testid={`${testid}-continue`}
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuotaCompleteCard({ kind, message }) {
  const testid = kind === "daily"
    ? "avz-daily-complete" : "avz-weekly-complete";
  const title = kind === "daily"
    ? "¡Práctica de hoy completada!"
    : "¡Excelente constancia esta semana!";
  const fallback = kind === "daily"
    ? "Guardamos tu progreso. Continúa con tu preparación en tu próxima sesión."
    : "Ya completaste tus sesiones de práctica. Puedes revisar tu progreso y continuar la próxima semana.";
  return (
    <div className="avz-card" data-testid={testid}>
      <h1 className="avz-h1" data-testid={`${testid}-title`}>{title}</h1>
      <p className="avz-lead avz-mt-4" data-testid={`${testid}-body`}>
        {message || fallback}
      </p>
      <div className="avz-row avz-mt-8">
        <Link to="/avanzo" className="avz-btn avz-btn-primary"
              data-testid={`${testid}-home`}>
          Volver a Inicio
        </Link>
        <Link to="/avanzo/progreso" className="avz-btn avz-btn-secondary"
              data-testid={`${testid}-progress`}>
          Ver mi progreso
        </Link>
      </div>
    </div>
  );
}

function CooldownCard({ remainingSeconds }) {
  const minutes = Math.max(1, Math.ceil((remainingSeconds || 0) / 60));
  return (
    <div className="avz-card" data-testid="avz-cooldown">
      <h1 className="avz-h1" data-testid="avz-cooldown-title">
        Tu sesión de práctica ya está completa
      </h1>
      <p className="avz-lead avz-mt-4">
        Podrás continuar más tarde. Mientras tanto puedes revisar tu progreso
        o explorar tus otras actividades.
      </p>
      <p className="avz-mute avz-mt-4" data-testid="avz-cooldown-eta">
        Estimado disponible en {minutes} min.
      </p>
      <div className="avz-row avz-mt-8">
        <Link to="/avanzo" className="avz-btn avz-btn-primary"
              data-testid="avz-cooldown-home">
          Volver a Inicio
        </Link>
        <Link to="/avanzo/progreso" className="avz-btn avz-btn-secondary"
              data-testid="avz-cooldown-progress">
          Ver mi progreso
        </Link>
      </div>
    </div>
  );
}

function SessionCompleteCard({ log, summary, voluntary = false }) {
  const [review, setReview]     = useState(null);
  const [loadingRev, setLoadingRev] = useState(false);
  const [revErr, setRevErr]     = useState(null);
  const sessionId = summary?.session_id
    || (log && log[0]?.sessionId)
    || null;

  const loadReview = async () => {
    if (!sessionId) { setRevErr("session_id_missing"); return; }
    setLoadingRev(true); setRevErr(null);
    try {
      const { data } = await avanzoApi.trainingReview(sessionId);
      setReview(data);
    } catch (e) {
      setRevErr(e?.response?.data?.detail || "review_error");
    } finally { setLoadingRev(false); }
  };

  if (review) {
    return <SessionReview review={review} onBack={() => setReview(null)} />;
  }

  const advancedSkills = [...new Set(
    (log || []).filter((x) => x.correct).map((x) => x.skill)
  )];
  const subjectsSet = new Set((log || []).map((x) => x.subjectLabel));
  const areasToReinforce = [...new Set(
    (log || []).filter((x) => !x.correct).map((x) => x.subjectLabel)
  )];

  return (
    <div className="avz-card" data-testid={voluntary
      ? "avz-session-summary" : "avz-session-complete"}>
      <h1 className="avz-h1" data-testid="avz-complete-title">
        ¡Sesión completada!
      </h1>
      <p className="avz-lead avz-mt-4">
        Hoy ya realizaste una buena práctica. Guardamos tu progreso para
        continuar desde aquí la próxima vez.
      </p>

      {subjectsSet.size > 0 && (
        <div className="avz-mt-8">
          <div className="avz-h3" data-testid="avz-complete-subjects">
            Materias practicadas
          </div>
          <ul className="avz-lead avz-mt-4">
            {[...subjectsSet].map((s) => <li key={s}>· {s}</li>)}
          </ul>
        </div>
      )}

      {advancedSkills.length > 0 && (
        <div className="avz-mt-8">
          <div className="avz-h3" data-testid="avz-complete-strengths">
            Fortalezas observadas
          </div>
          <ul className="avz-lead avz-mt-4">
            {advancedSkills.map((s) => <li key={s}>✓ {friendlySkill(s)}</li>)}
          </ul>
        </div>
      )}

      {areasToReinforce.length > 0 && (
        <div className="avz-mt-8">
          <div className="avz-h3" data-testid="avz-complete-review">
            Áreas que seguiremos reforzando
          </div>
          <ul className="avz-mute avz-mt-4">
            {areasToReinforce.map((s) => <li key={s}>· {s}</li>)}
          </ul>
        </div>
      )}

      {revErr && (
        <p className="avz-mute avz-mt-4" data-testid="avz-review-err"
            style={{ color: "#dc2626" }}>
          No pudimos cargar la retroalimentación. Intenta nuevamente.
        </p>
      )}

      <div className="avz-row avz-mt-8" style={{ display: "flex", gap: 10,
                                                   flexWrap: "wrap" }}>
        <button
          onClick={loadReview}
          disabled={loadingRev || !sessionId}
          className="avz-btn avz-btn-primary"
          data-testid="avz-complete-review-btn"
        >
          {loadingRev ? "Cargando…" : "Ver retroalimentación"}
        </button>
        <Link to="/avanzo" className="avz-btn avz-btn-secondary"
              data-testid="avz-complete-home">
          Volver a Inicio
        </Link>
      </div>
    </div>
  );
}


function SessionReview({ review, onBack }) {
  return (
    <div data-testid="avz-session-review">
      <div className="avz-card">
        <div className="avz-row-between">
          <div>
            <div className="avz-mute">Retroalimentación de tu sesión</div>
            <div className="avz-h2">
              {review.correct}/{review.total_items} correctas ·{" "}
              {review.percentage}%
            </div>
          </div>
          <button className="avz-btn avz-btn-secondary" onClick={onBack}
                   data-testid="avz-review-back">
            Volver al resumen
          </button>
        </div>
      </div>

      {review.items.map((it, idx) => (
        <div key={idx} className="avz-card avz-mt-4"
              data-testid={`avz-review-item-${idx}`}
              style={{ borderLeft: it.is_correct
                ? "4px solid #059669" : "4px solid #dc2626" }}>
          <div className="avz-mute">
            {subjectLabel(it.subject)} · Pregunta {idx + 1}
            {it.is_correct
              ? <span style={{ color: "#059669", marginLeft: 8, fontWeight: 700 }}>✓ Correcta</span>
              : <span style={{ color: "#dc2626", marginLeft: 8, fontWeight: 700 }}>✗ Incorrecta</span>}
          </div>
          {it.context && (
            <p className="avz-lead avz-mt-4" style={{ fontStyle: "italic" }}>
              {it.context}
            </p>
          )}
          <p className="avz-lead avz-mt-4" style={{ fontWeight: 600 }}>
            {it.question}
          </p>
          <ul className="avz-mt-4" style={{ listStyle: "none", padding: 0 }}>
            {["A", "B", "C", "D"].map((L) => {
              const text = it.options[L];
              if (!text) return null;
              const isCorrect = L === it.correct_answer;
              const isChosen  = L === it.your_answer;
              const bg = isCorrect ? "#ecfdf5"
                : isChosen ? "#fef2f2" : "transparent";
              const border = isCorrect ? "#059669"
                : isChosen ? "#dc2626" : "#e2e8f0";
              return (
                <li key={L} style={{ padding: "8px 12px", borderRadius: 10,
                      background: bg, border: `1px solid ${border}`,
                      marginBottom: 6, fontSize: 15 }}>
                  <b>{L}.</b> {text}
                  {isChosen && !isCorrect && (
                    <span style={{ color: "#dc2626", marginLeft: 8, fontSize: 12 }}>
                      (Tu respuesta)
                    </span>
                  )}
                  {isCorrect && (
                    <span style={{ color: "#059669", marginLeft: 8, fontSize: 12 }}>
                      (Respuesta correcta)
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

          {!it.is_correct && it.your_error_hint && (
            <div className="avz-mt-4" style={{ background: "#fff7ed",
                  border: "1px solid #fed7aa", borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700, color: "#9a3412",
                             marginBottom: 4 }}>
                ¿Dónde estuvo el error?
              </div>
              <p className="avz-lead" style={{ color: "#7c2d12", margin: 0 }}>
                {it.your_error_hint}
              </p>
            </div>
          )}

          {it.rationale_correct && (
            <div className="avz-mt-4" style={{ background: "#ecfdf5",
                  border: "1px solid #a7f3d0", borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700, color: "#065f46",
                             marginBottom: 4 }}>
                Por qué la respuesta correcta es {it.correct_answer}
              </div>
              <p className="avz-lead" style={{ color: "#064e3b", margin: 0 }}>
                {it.rationale_correct}
              </p>
            </div>
          )}

          {(it.explicit_teaching || it.guided_explanation) && (
            <div className="avz-mt-4" style={{ background: "#f8fafc",
                  border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700, color: "#0f172a",
                             marginBottom: 4 }}>
                Repaso del concepto
              </div>
              {it.explicit_teaching && (
                <p className="avz-lead" style={{ margin: "6px 0" }}>
                  {it.explicit_teaching}
                </p>
              )}
              {it.guided_explanation && (
                <p className="avz-mute" style={{ margin: "6px 0" }}>
                  {it.guided_explanation}
                </p>
              )}
            </div>
          )}
        </div>
      ))}

      <div className="avz-card avz-mt-4" style={{ display: "flex", gap: 10,
              flexWrap: "wrap", justifyContent: "space-between",
              alignItems: "center" }}>
        <p className="avz-mute" style={{ margin: 0 }}>
          Guarda esta retroalimentación como referencia. Cada error identificado
          es una oportunidad de mejora para AVANZO.
        </p>
        <Link to="/avanzo" className="avz-btn avz-btn-primary"
              data-testid="avz-review-back-home">
          Volver a Inicio
        </Link>
      </div>
    </div>
  );
}

function subjectLabel(key) {
  const m = {
    matematica: "Precálculo",
    sociales: "Ciudadanía y Valores",
    ciencias: "Ciencia y Tecnología",
    lenguaje: "Lenguaje y Literatura",
  };
  return m[key] || key;
}

function friendlySkill(raw) {
  return String(raw).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}


// Fase 8 — Banner del modo del día en Training
function TrainModeBanner({ mode }) {
  const t = mode.copy?.training || {};
  const tShort = mode.copy?.training_short || {};
  const rec = mode.recommended_session_minutes_min;
  const recMax = mode.recommended_session_minutes_max;
  return (
    <section
      className="avz-card avz-mode-banner"
      data-testid={`avz-train-mode-${mode.mode.toLowerCase()}`}
      style={{
        borderLeft: "4px solid #0f172a", background: "#f8fafc",
        marginBottom: 16, padding: 12,
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 16 }}>
        <span className="avz-mode-headline-full">{t.title}</span>
        <span
          className="avz-mode-headline-short"
          data-testid="avz-train-mode-title-short"
          style={{ display: "none" }}
        >{tShort.title}</span>
      </div>
      {t.subtitle && (
        <p className="avz-mute avz-mt-4" data-testid="avz-train-mode-subtitle">
          {t.subtitle}
        </p>
      )}
      {rec && recMax && (
        <p
          className="avz-mute avz-mt-4"
          data-testid="avz-train-mode-recommendation"
        >
          Práctica sugerida: {rec}–{recMax} minutos. Puedes terminar antes si lo prefieres.
        </p>
      )}
    </section>
  );
}
